import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useGovernance } from '../../context/GovernanceContext';
import { DIMENSIONS } from '../../data/questions';
import { formatScore, getMaturity } from '../../utils/scoring';
import ScoreBar from '../shared/ScoreBar';
import MaturityLabel from '../shared/MaturityLabel';

export default function ClientOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    clients,
    loading,
    getClientDimensionScore,
    getClientOverallScore,
    getClientActions,
    getClientDeployments,
  } = useGovernance();

  const client = clients.find((c) => c.id === id);

  if (loading) return <div className="text-mid-grey text-sm">Loading…</div>;
  if (!client) return (
    <div className="text-center py-16">
      <p className="text-mid-grey">Client not found.</p>
      <Link to="/clients" className="text-brand-teal text-sm hover:underline mt-2 block">
        ← Back to clients
      </Link>
    </div>
  );

  const overall = getClientOverallScore(client.id);
  const allActions = getClientActions(client.id);
  const deployments = getClientDeployments(client.id);
  const deployedCount = deployments.filter((d) => d.status === 'deployed' || d.status === 'completed').length;

  // Radar chart data
  const radarData = DIMENSIONS.map((d) => ({
    dimension: d.shortLabel,
    score: getClientDimensionScore(client.id, d.num) ?? 0,
    fullMark: 5,
  }));

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-mid-grey mb-5">
        <Link to="/clients" className="hover:text-brand-navy">Clients</Link>
        <span>/</span>
        <span className="text-brand-navy font-medium">{client.name}</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => navigate(`/clients/${client.id}/guides`)}
            className="text-xs font-semibold text-brand-navy border border-brand-navy rounded-lg px-3 py-1 hover:bg-brand-navy hover:text-white transition-colors"
          >
            Guides →
          </button>
          <button
            onClick={() => navigate(`/clients/${client.id}/project`)}
            className="text-xs font-semibold text-brand-navy border border-brand-navy rounded-lg px-3 py-1 hover:bg-brand-navy hover:text-white transition-colors"
          >
            Project plan →
          </button>
          <button
            onClick={() => navigate(`/clients/${client.id}/report`)}
            className="text-xs font-semibold text-brand-teal border border-brand-teal rounded-lg px-3 py-1 hover:bg-brand-teal hover:text-white transition-colors"
          >
            Export report →
          </button>
        </div>
      </nav>

      {/* Client header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-brand-navy">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {client.industry && (
                <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                  {client.industry}
                </span>
              )}
              {client.contact_name && (
                <span className="text-sm text-mid-grey">{client.contact_name}</span>
              )}
              {client.contact_email && (
                <a
                  href={`mailto:${client.contact_email}`}
                  className="text-sm text-brand-teal hover:underline"
                >
                  {client.contact_email}
                </a>
              )}
              {deployedCount > 0 && (
                <span className="text-xs text-mid-grey">{deployedCount} guide{deployedCount !== 1 ? 's' : ''} deployed</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-brand-navy">{formatScore(overall)}</div>
            <MaturityLabel score={overall} />
            <div className="text-xs text-mid-grey mt-0.5">Overall maturity</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Radar chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-brand-navy mb-4">Maturity radar</h2>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis
                dataKey="dimension"
                tick={{ fill: '#6B7280', fontSize: 11 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 5]}
                tickCount={6}
                tick={{ fontSize: 9, fill: '#9CA3AF' }}
              />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#0F2D6B"
                fill="#0F2D6B"
                fillOpacity={0.25}
                strokeWidth={2}
              />
              <Tooltip
                formatter={(v) => [v === 0 ? 'Not assessed' : v.toFixed(1), 'Score']}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension summary cards */}
        <div className="space-y-3">
          {DIMENSIONS.map((dim) => {
            const score = getClientDimensionScore(client.id, dim.num);
            const maturity = getMaturity(score);
            const dimActions = allActions.filter((a) => a.dimension === dim.num);
            const doneCount = dimActions.filter((a) => a.status === 'done').length;

            return (
              <div
                key={dim.num}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4"
                style={{ borderLeft: `4px solid ${dim.color}` }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-brand-navy truncate">
                      D{dim.num} · {dim.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ScoreBar score={score} className="flex-1" />
                    <span className="text-sm font-bold" style={{ color: maturity.color }}>
                      {formatScore(score)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <MaturityLabel score={score} size="sm" />
                    {dimActions.length > 0 && (
                      <span className="text-xs text-mid-grey">
                        {doneCount}/{dimActions.length} actions done
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/clients/${client.id}/dimension/${dim.num}`)}
                  className="text-xs font-semibold text-brand-teal hover:underline whitespace-nowrap"
                >
                  Open →
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
